import React, { useEffect, useState } from "react";
import { Toast as ToastType } from "../types";

interface ToastProps {
  toast: ToastType;
  onRemove: (id: string) => void;
}

const Toast: React.FC<ToastProps> = ({ toast, onRemove }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    // Trigger entrance animation
    const timer = setTimeout(() => setIsVisible(true), 10);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (toast.duration && toast.type === "undo") {
      // Animate progress bar for undo toasts
      const startTime = Date.now();
      const interval = setInterval(() => {
        setProgress((prev) => {
          const elapsed = Date.now() - startTime;
          const remaining = Math.max(
            0,
            100 - (elapsed / toast.duration!) * 100
          );
          return remaining;
        });
      }, 50);

      return () => clearInterval(interval);
    }
  }, [toast.duration, toast.type]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => onRemove(toast.id), 300); // Wait for exit animation
  };

  const getToastStyles = () => {
    const baseStyles =
      "rounded-lg shadow-lg p-4 flex items-center justify-between min-w-[320px] max-w-md";

    switch (toast.type) {
      case "success":
        return `${baseStyles} bg-green-500 text-white`;
      case "error":
        return `${baseStyles} bg-red-500 text-white`;
      case "info":
        return `${baseStyles} bg-blue-500 text-white`;
      case "warning":
        return `${baseStyles} bg-yellow-500 text-white`;
      case "undo":
        return `${baseStyles} bg-neutral-800 text-white`;
      default:
        return `${baseStyles} bg-neutral-600 text-white`;
    }
  };

  const getIcon = () => {
    switch (toast.type) {
      case "success":
        return "✓";
      case "error":
        return "✕";
      case "info":
        return "ℹ";
      case "warning":
        return "\u26A0";
      case "undo":
        return "↶";
      default:
        return "•";
    }
  };

  /**
   * Returns ARIA attributes for accessibility based on toast type.
   * Error toasts are assertive (immediate announcement), others are polite.
   */
  const getAriaAttributes = () => {
    if (toast.type === "error" || toast.type === "warning") {
      return {
        role: "alert" as const,
        "aria-live": "assertive" as const,
        "aria-atomic": true,
      };
    }
    return {
      role: "status" as const,
      "aria-live": "polite" as const,
      "aria-atomic": true,
    };
  };

  const ariaAttributes = getAriaAttributes();

  return (
    <div
      className={`transition-all duration-300 ease-in-out transform ${
        isVisible
          ? "translate-y-0 opacity-100 scale-100"
          : "translate-y-2 opacity-0 scale-95"
      }`}
    >
      <div
        className={getToastStyles()}
        data-testid="toast-notification"
        data-toast-type={toast.type}
        {...ariaAttributes}
      >
        <div className="flex items-center space-x-3 flex-1">
          <span className="text-lg font-medium" aria-hidden="true">{getIcon()}</span>
          <span className="text-sm font-medium">{toast.message}</span>
        </div>

        <div className="flex items-center space-x-2 ml-4">
          {toast.action && (
            <button
              onClick={toast.action.onClick}
              className="px-3 py-1 bg-white bg-opacity-20 hover:bg-opacity-30 rounded text-sm font-medium transition-colors"
            >
              {toast.action.label}
            </button>
          )}

          <button
            onClick={handleClose}
            className="p-1 hover:bg-white hover:bg-opacity-20 rounded transition-colors"
            title="Zamknij"
            aria-label="Zamknij powiadomienie"
          >
            <span className="text-sm" aria-hidden="true">✕</span>
          </button>
        </div>
      </div>

      {/* Progress bar for undo toasts */}
      {toast.type === "undo" && toast.duration && (
        <div className="w-full bg-white bg-opacity-20 rounded-full h-1 mt-2">
          <div
            className="bg-white h-1 rounded-full transition-all duration-75 ease-linear"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
    </div>
  );
};

export default Toast;
